/**
 * Field descriptors, working-row model, and the editable cell renderer for the
 * bulk-import PreviewGrid. Extracted so PreviewGrid.tsx stays focused on the
 * grid orchestration (state, dry-run debounce, commit).
 */

import type { CSSProperties } from 'react'
import type { BulkImportRow } from '../../api/hr-bulk-import'

// ── Field descriptors ──────────────────────────────────────────────────────────

export type FieldKey = keyof BulkImportRow

export type FieldKind = 'text' | 'date' | 'enum' | 'bool' | 'int'

export interface FieldDescriptor {
  key: FieldKey
  label: string
  kind: FieldKind
  /** options for enum fields (value === display) */
  options?: ReadonlyArray<string>
}

// Order chosen for readability: identity first, then job/placement, demographics,
// lifecycle. Action + Note are rendered as trailing columns by the grid itself.
export const FIELDS: ReadonlyArray<FieldDescriptor> = [
  { key: 'internalId', label: 'Employee ID', kind: 'text' },
  { key: 'email', label: 'Email', kind: 'text' },
  { key: 'name', label: 'Name', kind: 'text' },
  { key: 'preferredName', label: 'Preferred name', kind: 'text' },
  { key: 'teamName', label: 'Team', kind: 'text' },
  { key: 'jobTitle', label: 'Job title', kind: 'text' },
  // Text, not enum: Role may be a delimited multi-role list (e.g. "hod;hr;
  // manager"), which an enum <select> can't represent — it would blank to
  // "—". Values are validated (per token) by the parser + backend.
  { key: 'role', label: 'Role', kind: 'text' },
  {
    key: 'employmentType',
    label: 'Employment type',
    kind: 'enum',
    options: ['full_time', 'contractual'],
  },
  { key: 'gender', label: 'Gender', kind: 'enum', options: ['F', 'M', 'X'] },
  { key: 'race', label: 'Race', kind: 'enum', options: ['chinese', 'indian', 'malay', 'other'] },
  // Free text — religion is stored verbatim (e.g. "sikh"), not a fixed enum,
  // so an enum <select> can't render values outside the known set. A text
  // input shows + edits any religion.
  { key: 'religion', label: 'Religion', kind: 'text' },
  { key: 'birthDate', label: 'Birth date', kind: 'date' },
  { key: 'joinDate', label: 'Join date', kind: 'date' },
  { key: 'married', label: 'Married', kind: 'bool' },
  { key: 'children', label: 'Children', kind: 'int' },
  { key: 'hometown', label: 'Hometown', kind: 'text' },
  { key: 'distanceKm', label: 'Distance (km)', kind: 'int' },
  { key: 'onProbation', label: 'On probation', kind: 'bool' },
  { key: 'probationEndDate', label: 'Confirmation date', kind: 'date' },
  { key: 'contractEndDate', label: 'Contract end', kind: 'date' },
  { key: 'lastWorkingDay', label: 'Last working day', kind: 'date' },
  { key: 'isManager', label: 'Is manager', kind: 'bool' },
  { key: 'isHod', label: 'Is HOD', kind: 'bool' },
  { key: 'managerName', label: 'Manager name', kind: 'text' },
  { key: 'reportsToEmail', label: 'Reports-to email', kind: 'text' },
  { key: 'active', label: 'Active', kind: 'bool' },
]

// ── Working-row model ───────────────────────────────────────────────────────────

export interface WorkingRow {
  /** Stable id — the original parse index. Never changes. */
  id: number
  /** The pristine parsed row, kept for changed-cell comparison. */
  original: BulkImportRow
  /** The current edited copy sent to the backend. */
  current: BulkImportRow
  skipped: boolean
}

/** Cell value as a string for an <input>/<select> (undefined → ''). */
export function toCellString(value: BulkImportRow[FieldKey]): string {
  if (value === undefined || value === null) return ''
  if (typeof value === 'boolean') return value ? 'true' : 'false'
  return String(value)
}

/** Coerce a raw input string back into the BulkImportRow field shape. */
function fromCellString(kind: FieldKind, raw: string): BulkImportRow[FieldKey] {
  const t = raw.trim()
  if (t === '') return undefined
  if (kind === 'bool') return t === 'true' ? true : t === 'false' ? false : undefined
  if (kind === 'int') {
    const n = Number(t)
    return Number.isInteger(n) && n >= 0 ? n : undefined
  }
  return t
}

/** Immutably apply an edit to a working row, dropping the key when cleared. */
export function applyEdit(
  row: WorkingRow,
  key: FieldKey,
  kind: FieldKind,
  raw: string,
): WorkingRow {
  const coerced = fromCellString(kind, raw)
  if (coerced === undefined) {
    const copy = { ...row.current }
    delete copy[key]
    return { ...row, current: copy as BulkImportRow }
  }
  return { ...row, current: { ...row.current, [key]: coerced } as BulkImportRow }
}

function isChanged(row: WorkingRow, key: FieldKey): boolean {
  return toCellString(row.current[key]) !== toCellString(row.original[key])
}

// ── Cell renderer ───────────────────────────────────────────────────────────────

const CHANGED_COLOR = 'var(--steel-blue)'
const CHANGED_BG = 'color-mix(in srgb, var(--steel-blue) 12%, transparent)'

const INPUT_STYLE: CSSProperties = {
  width: '100%',
  minWidth: 90,
  padding: '4px 6px',
  fontSize: 12,
  border: '1px solid var(--border)',
  borderRadius: 4,
  background: 'var(--surface)',
  color: 'inherit',
  fontFamily: 'inherit',
}

interface CellProps {
  field: FieldDescriptor
  row: WorkingRow
  onEdit: (id: number, key: FieldKey, kind: FieldKind, raw: string) => void
}

export function Cell({ field, row, onEdit }: CellProps): JSX.Element {
  const value = toCellString(row.current[field.key])
  const changed = isChanged(row, field.key)
  const cellStyle: CSSProperties = {
    padding: '4px 8px',
    borderBottom: '1px solid var(--border)',
    color: changed ? CHANGED_COLOR : 'var(--text-2)',
    background: changed ? CHANGED_BG : undefined,
  }
  const inputStyle: CSSProperties = {
    ...INPUT_STYLE,
    color: changed ? CHANGED_COLOR : 'var(--text)',
    borderColor: changed ? 'var(--steel-blue)' : 'var(--border)',
  }
  const handle = (raw: string): void => onEdit(row.id, field.key, field.kind, raw)

  let control: JSX.Element
  if (field.kind === 'enum') {
    control = (
      <select aria-label={field.label} value={value} onChange={(e) => handle(e.target.value)} style={inputStyle}>
        <option value="">—</option>
        {field.options?.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    )
  } else if (field.kind === 'bool') {
    control = (
      <select aria-label={field.label} value={value} onChange={(e) => handle(e.target.value)} style={inputStyle}>
        <option value="">—</option>
        <option value="true">true</option>
        <option value="false">false</option>
      </select>
    )
  } else if (field.kind === 'int') {
    control = (
      <input
        type="number"
        min={0}
        aria-label={field.label}
        value={value}
        onChange={(e) => handle(e.target.value)}
        style={inputStyle}
      />
    )
  } else {
    control = (
      <input
        type={field.kind === 'date' ? 'date' : 'text'}
        aria-label={field.label}
        value={value}
        onChange={(e) => handle(e.target.value)}
        style={inputStyle}
      />
    )
  }

  return (
    <td data-testid={`cell-${field.key}`} data-changed={changed ? 'true' : 'false'} style={cellStyle}>
      {control}
    </td>
  )
}
